/**
 * Compact textual serialization of the timeline for the system prompt
 * (docs/agent.md §3).
 *
 * Design constraints, in priority order:
 *  1. Real ids, always. The model must reference clips by an id that exists,
 *     so ids lead every clip entry and are never elided.
 *  2. Cheap. Budget ~1500 tokens. JSON would spend most of that on punctuation
 *     and repeated key names, so this is line-oriented prose-ish instead.
 *  3. Honest about elision. Past ~60 clips the middle of a track is replaced
 *     with an explicit "… N more clips …" marker rather than silently truncated
 *     — a model that cannot see a clip must at least know it exists.
 */
import { clipDuration, clipEnd, isAudioClip, totalDuration } from '../../format/helpers';
import type { AnyClip, Edit, TextClip, Track, VideoClip } from '../../format/types';

/** Clips shown per track before the middle is elided. */
const MAX_CLIPS_PER_TRACK = 30;
/** Kept at each end when eliding. */
const HEAD_TAIL = 12;

export interface DescribeOptions {
  playhead?: number;
  selection?: string[];
  maxClipsPerTrack?: number;
  /**
   * Only list clips intersecting [from, to] (timeline seconds). This is how a
   * model pages into the elided middle of a long track — without it, clip ids
   * past the head/tail are unreachable, and every edit tool anchors by id.
   */
  window?: { from: number; to: number };
}

export function describeEdit(edit: Edit, opts: DescribeOptions = {}): string {
  const lines: string[] = [];
  const { settings } = edit;
  const header =
    `Project ${settings.width}x${settings.height}@${settings.fps}. ` +
    `Duration ${fmt(totalDuration(edit))}s.`;
  const bits = [header];
  if (opts.playhead !== undefined) bits.push(`Playhead ${fmt(opts.playhead)}s.`);
  if (opts.selection?.length) bits.push(`Selection: ${opts.selection.join(', ')}.`);
  else if (opts.selection) bits.push('Selection: none.');
  if (opts.window) bits.push(`Showing ${fmt(opts.window.from)}–${fmt(opts.window.to)}s.`);
  lines.push(bits.join(' '));

  if (!edit.tracks.length) {
    lines.push('(no tracks)');
    return lines.join('\n');
  }

  for (const track of edit.tracks) {
    lines.push(
      describeTrack(track, edit, opts.maxClipsPerTrack ?? MAX_CLIPS_PER_TRACK, opts.window),
    );
  }
  return lines.join('\n');
}

function describeTrack(
  track: Track,
  edit: Edit,
  maxClips: number,
  window?: { from: number; to: number },
): string {
  const attrs: string[] = [track.kind];
  if (track.muted) attrs.push('muted');
  if (track.solo) attrs.push('solo');
  if (track.kind === 'audio' && track.gain !== 1) attrs.push(`gain ${fmt(track.gain)}`);
  if (track.kind === 'video' && track.blend) attrs.push(`blend ${track.blend}`);
  if (track.kind === 'video' && track.opacity !== undefined && track.opacity !== 1)
    attrs.push(`opacity ${fmt(track.opacity)}`);

  const all = track.clips as AnyClip[];
  // The track ID must be here, not just the label. Tools take track ids, and a
  // model shown only "V1" will pass "V1" — which silently falls back to the
  // first track of the kind at edit time and then fails at replay time. Caught
  // by the multitrack eval, where the model addressed a track as "V1".
  const head = `${track.label} [${track.id}] (${attrs.join(', ')}):`;
  if (!all.length) return `${head} empty`;

  // A time window keeps elision honest: clips wholly before/after it are
  // counted, clips intersecting it are listed.
  let clips = all;
  let earlier = 0;
  let later = 0;
  if (window) {
    const { from, to } = window;
    clips = all.filter((c) => clipEnd(c) > from && c.start < to);
    earlier = all.filter((c) => clipEnd(c) <= from).length;
    later = all.length - earlier - clips.length;
  }

  const rendered =
    clips.length <= maxClips
      ? clips.map((c) => describeClip(c, edit))
      : [
          ...clips.slice(0, HEAD_TAIL).map((c) => describeClip(c, edit)),
          `… ${clips.length - HEAD_TAIL * 2} more clips …`,
          ...clips.slice(-HEAD_TAIL).map((c) => describeClip(c, edit)),
        ];
  if (!rendered.length) rendered.push('(none in this range)');
  if (earlier) rendered.unshift(`… ${earlier} earlier clips …`);
  if (later) rendered.push(`… ${later} later clips …`);
  return `${head} [${rendered.join(' | ')}]`;
}

function describeClip(clip: AnyClip, edit: Edit): string {
  const span = `${fmt(clip.start)}–${fmt(clipEnd(clip))}`;
  if (isText(clip)) {
    return `${clip.id} text ${JSON.stringify(truncate(clip.text, 40))} ${span}`;
  }
  if (isAudioClip(clip)) {
    const parts = [`${clip.id} "${sourceTitle(edit, clip.source)}"`, span];
    parts.push(`(src ${fmt(clip.in)}–${fmt(clip.out)})`);
    if ((clip.speed ?? 1) !== 1) parts.push(`${fmt(clip.speed ?? 1)}x`);
    if (clip.gain !== 1) parts.push(`gain ${fmt(clip.gain)}`);
    return parts.join(' ');
  }
  const v = clip as VideoClip;
  const parts = [`${v.id} "${sourceTitle(edit, v.source)}"`, span];
  parts.push(`(src ${fmt(v.in)}–${fmt(v.out)})`);
  if (v.speed !== 1) parts.push(`${fmt(v.speed)}x`);
  if (v.muted) parts.push('muted');
  else if (v.volume !== 1) parts.push(`vol ${fmt(v.volume)}`);
  if (v.fadeIn) parts.push(`fadeIn ${fmt(v.fadeIn)}`);
  if (v.fadeOut) parts.push(`fadeOut ${fmt(v.fadeOut)}`);
  if (v.transitionIn) parts.push(`${v.transitionIn.type}→${fmt(v.transitionIn.duration)}s`);
  if (v.effects.length) parts.push(`fx:${v.effects.map((e) => e.type).join('+')}`);
  // Animated params are worth a mention (the model may be asked to adjust them)
  // but their keyframes are not — that is what a dedicated read would be for.
  const animated = animatedParamNames(v);
  if (animated.length) parts.push(`anim:${animated.join('+')}`);
  return parts.join(' ');
}

function animatedParamNames(clip: VideoClip): string[] {
  const names: string[] = [];
  for (const [key, value] of Object.entries(clip.transform)) {
    if (value !== null && typeof value === 'object' && 'keyframes' in value) {
      names.push(`transform.${key}`);
    }
  }
  for (const effect of clip.effects) {
    for (const [key, value] of Object.entries(effect)) {
      if (value !== null && typeof value === 'object' && 'keyframes' in value) {
        names.push(`effects.${effect.id}.${key}`);
      }
    }
  }
  return names;
}

function sourceTitle(edit: Edit, sourceId: string): string {
  const src = edit.sources[sourceId];
  if (!src) return '?';
  if (src.kind === 'pandora-video') return truncate(src.title, 40);
  return 'image';
}

function isText(clip: AnyClip): clip is TextClip {
  return 'kind' in clip && clip.kind === 'text';
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** One decimal place, without a trailing ".0" on whole seconds. */
function fmt(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export { clipDuration };
